import axios, { AxiosError } from 'axios'
import { initializeApp } from 'firebase/app'
import { getAuth, onAuthStateChanged, User } from 'firebase/auth'

let isForceLoggingOut = false

const api = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  // baseURL: 'http://localhost:8000',
  timeout: 15000,
})

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)

export function initFirebase() {
  initializeApp(firebaseConfig)
}

const waitForCurrentUser = async (timeoutMs = 1500): Promise<User | null> => {
  if (auth.currentUser) return auth.currentUser

  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      unsubscribe()
      resolve(auth.currentUser)
    }, timeoutMs)

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      window.clearTimeout(timeout)
      unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Extract a user-friendly message from a backend/axios error.
 * Handles: detail string, message string, and validation errors (field -> list of messages).
 */
export function getApiErrorMessage(error: unknown): string {
  const fallback = 'Something went wrong. Please try again.'
  if (!error || typeof error !== 'object') return fallback

  const ax = error as AxiosError<{
    detail?: string | string[] | Record<string, unknown>
    message?: string
    [key: string]: unknown
  }>
  const data = ax.response?.data

  if (data && typeof data === 'object') {
    const detail = data.detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0]
      if (typeof first === 'string') return first
      // e.g. FastAPI validation: [{ msg: "User with this email already exists." }]
      if (first && typeof first === 'object' && 'msg' in first && typeof (first as { msg?: string }).msg === 'string') {
        return (first as { msg: string }).msg
      }
      return JSON.stringify(first)
    }
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const parts = Object.entries(detail)
        .filter(([, v]) => Array.isArray(v) ? v.length > 0 : v != null && v !== '')
        .map(([k, v]) => (Array.isArray(v) ? `${k}: ${(v as unknown[]).join(', ')}` : `${k}: ${v}`))
      if (parts.length > 0) return parts.join('. ')
    }

    const message = data.message
    if (typeof message === 'string' && message.trim()) return message
    const err = data.error
    if (typeof err === 'string' && err.trim()) return err

    const topLevelParts = Object.entries(data)
      .filter(([, v]) => Array.isArray(v) ? v.length > 0 : v != null && v !== '')
      .map(([k, v]) => {
        if (Array.isArray(v)) return `${k}: ${(v as unknown[]).join(', ')}`
        if (typeof v === 'object') return `${k}: ${JSON.stringify(v)}`
        return `${k}: ${v}`
      })
    if (topLevelParts.length > 0) return topLevelParts.join('. ')
  }

  const status = (error as AxiosError).response?.status
  if (status === 400) return 'Invalid request. Please check your input.'
  if (status === 401) return 'Please sign in again.'
  if (status === 403) return 'You do not have permission to do this.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 422) {
    const d = data as { detail?: string | unknown[] } | undefined
    if (d?.detail && typeof d.detail === 'string') return d.detail
    if (d?.detail && Array.isArray(d.detail) && d.detail.length > 0) {
      const first = d.detail[0]
      if (typeof first === 'string') return first
      if (first && typeof first === 'object' && 'msg' in first && typeof (first as { msg?: string }).msg === 'string') {
        return (first as { msg: string }).msg
      }
    }
    return 'Validation error. Please check your input.'
  }
  if (status && status >= 500) return 'Server error. Please try again later.'

  const msg = (error as Error).message
  return typeof msg === 'string' && msg.trim() ? msg : fallback
}

api.interceptors.request.use(async (config) => {
  const user = await waitForCurrentUser()
  if (user) {
    // Force refresh if token is within 5 minutes of expiry
    const tokenResult = await user.getIdTokenResult()
    const expiresAt = new Date(tokenResult.expirationTime).getTime()
    const needsRefresh = expiresAt - Date.now() < 5 * 60 * 1000
    config.headers['Authorization'] = `Bearer ${await user.getIdToken(needsRefresh)}`
  }

  return config
})

const forceLogoutNow = async () => {
  if (isForceLoggingOut) return
  isForceLoggingOut = true

  try {
    await auth.signOut()
  } catch {
    // Ignore sign out errors and continue local cleanup.
  }

  localStorage.removeItem('is_admin')
  localStorage.removeItem('role')
  localStorage.removeItem('backend_role')

  try {
    sessionStorage.removeItem('mfa_verified_session')
  } catch {
    // Ignore sessionStorage access errors (for example, restricted browser storage settings).
  }

  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login'
  }
}

const tokenExpiredRegex = /(token\s+is\s+expired|token\s+expired|expired\s+token|jwt\s+expired|signature\s+has\s+expired)/i

const hasTokenExpiredMessage = (payload: unknown): boolean => {
  if (payload == null) return false
  if (typeof payload === 'string') return tokenExpiredRegex.test(payload)

  try {
    return tokenExpiredRegex.test(JSON.stringify(payload))
  } catch {
    return false
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status
    const data = error?.response?.data
    const code = String(data?.code || '').toLowerCase()
    const message = String(data?.message || '')
    const detail = data?.detail

    const isAuthStatus = status === 401 || status === 403
    const isTokenExpired =
      isAuthStatus &&
      (code.includes('token_expired') ||
        hasTokenExpiredMessage(detail) ||
        hasTokenExpiredMessage(message) ||
        hasTokenExpiredMessage(data))

    if (isTokenExpired) {
      await forceLogoutNow()
    }

    ;(error as Error).message = getApiErrorMessage(error)
    return Promise.reject(error)
  }
)

export default api

/**
 * Unwrap common API response shapes into clean data.
 *
 * Handles:
 *   - { data: [...] }           → [...]
 *   - { data: { results: [...] } } → { results: [...], count, next, ... }
 *   - { results: [...] }        → [...]
 *   - [...]                     → [...]  (already plain array)
 *
 * @param res  The axios response object
 * @param listOnly  If true, always returns an array (ignores pagination metadata)
 * @returns  Unwrapped data (array, object, or paginated object)
 */
export function unwrapApiResponse(res: any, listOnly = false): any {
  const raw = res?.data
  if (raw == null) return listOnly ? [] : raw

  if (Array.isArray(raw)) return raw

  if (typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data
    if (Array.isArray(raw.results)) return listOnly ? raw.results : raw
    if (listOnly) return []
    return raw
  }

  return listOnly ? [] : raw
}

export function buildFormData(fields: Record<string, unknown>): FormData {
  const fd = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      fd.append(key, JSON.stringify(value))
    } else if (value instanceof Blob) {
      fd.append(key, value)
    } else if (typeof value === 'boolean' || typeof value === 'number') {
      fd.append(key, String(value))
    } else {
      fd.append(key, value as string)
    }
  }
  return fd
}
